import React, { useEffect, useState } from "react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { Logistic } from "src/services/types";
import { Link } from "react-router-dom";
import AdminService from "src/services/admin.services";
import Modal from "./Modal";
import { toast } from "react-toastify";

const adminService = new AdminService();

// In LogisticDetails.tsx
export interface LogisticDetailsProps {
  logistic: Logistic;
  onClose: () => void;
  handleStatusChange: (logistic: Logistic, newStatus: string) => void;
  successMessage: string | null;
  error: string | null;
  refetchSelectedOrder: () => void;
}
const LogisticDetails: React.FC<LogisticDetailsProps> = ({
  logistic,
  onClose,
  successMessage,
  error,
  refetchSelectedOrder,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState<"success" | "error">("success");
  const center = {
    lat: logistic?.pickup_location.latitude || 0,
    lng: logistic?.pickup_location.longitude || 0,
  };
  const handleStatusChange = async (id: string) => {
    try {
      const response = await adminService.updateParcelStatus(id);

      if (response?.message === "parcel updated successfully") {
        toast.success("Parcel order cancelled successfully");
        onClose();
        refetchSelectedOrder();
      } else {
        toast.error("Error cancelling parcel order");
        onClose();
        refetchSelectedOrder();
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Parcel order cannot be cancelled";
      toast.error(errorMessage);
      onClose();
      refetchSelectedOrder();
    }
  };

  const handleDeliverOrder = async (id: string) => {
    try {
      const response = await adminService.updateOrderAsDelivered({
        parcel_id: id,
      });
      if (response?.message === "order delivered successfully") {
        setModalMessage("Order delivered successfully");
        setModalType("success");
        setIsModalOpen(true);
        refetchSelectedOrder();
      } else {
        setModalMessage(response.error);
        setModalType("error");
        setIsModalOpen(true);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        "Order can not be delivered at the moment";
      setModalMessage(errorMessage);
      setModalType("error");
      setIsModalOpen(true);
      refetchSelectedOrder();
    }
  };

  const handleCancelDeliveryRequest = async (id: string) => {
    try {
      const response = await adminService.cancelDeliveryRequest({
        parcel_id: id,
        order_id: "",
      });
      if (response?.message === "delivery request cancelled successfully") {
        setModalMessage("Delivery request cancelled successfully");
        setModalType("success");
        setIsModalOpen(true);
        refetchSelectedOrder();
      } else {
        setModalMessage(response.error);
        setModalType("error");
        setIsModalOpen(true);
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || "Error cancelling delivery request";
      setModalMessage(errorMessage);
      setModalType("error");
      setIsModalOpen(true);
      refetchSelectedOrder();
    }
  };
  useEffect(() => {
    // Disable scroll when modal opens
    document.body.classList.add("no-scroll");
    return () => {
      // Restore scroll when modal closes
      document.body.classList.remove("no-scroll");
    };
  }, []);
  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white p-6 pb-4 border-b border-gray-200 flex justify-between items-start z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Delivery #{logistic?.trackingId}
            </h2>
            <div className="flex items-center mt-2 space-x-3">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${logistic?.order_status === "delivered"
                    ? "bg-green-100 text-green-800"
                    : logistic?.order_status === "cancelled"
                      ? "bg-red-100 text-red-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
              >
                {logistic?.order_status}
              </span>
              <span className="text-sm text-gray-500">
                {new Date(logistic?.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="flex space-x-2">
            {["enroute", "picked_up", "arrived", "preparing"].includes(
              logistic?.order_status
            ) && (
                <button
                  onClick={() => handleCancelDeliveryRequest(logistic._id)}
                  className="bg-yellow-500 text-white px-4 py-1.5 rounded-md hover:bg-amber-700 transition"
                >
                  Cancel delivery request
                </button>
              )}
            {logistic?.order_status === "arrived" && (
              <button
                onClick={() => handleDeliverOrder(logistic._id)}
                className="bg-yellow-500 text-white px-4 py-1.5 rounded-md hover:bg-amber-700 transition"
              >
                Deliver parcel
              </button>
            )}
            {logistic?.order_status !== "delivered" &&
              logistic?.order_status !== "cancelled" && (
                <div className="">
                  <button
                    onClick={() => handleStatusChange(logistic?._id)}
                    className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-6 rounded-lg transition flex items-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Cancel Order
                  </button>
                </div>
              )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition p-1 -mt-2 -mr-2"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="px-6 pt-4">
          {successMessage && (
            <div className="bg-green-50 text-green-800 p-3 rounded-lg mb-4 flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {successMessage}
            </div>
          )}
          {error && (
            <div className="bg-red-50 text-red-800 p-3 rounded-lg mb-4 flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Timeline & Map */}
            <div>
              <div className="bg-gray-50 rounded-lg p-5 mb-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  Delivery Timeline
                </h3>
                <div className="space-y-4">
                  <TimelineItem
                    title="Order Created"
                    date={logistic?.created_at}
                    active
                  />
                  <TimelineItem
                    title="Picked Up"
                    date={logistic?.picked_up_time}
                    active={!!logistic?.picked_up_time}
                  />
                  <TimelineItem
                    title="Enroute"
                    date={logistic?.enroute_time}
                    active={!!logistic?.enroute_time}
                  />
                  <TimelineItem
                    title="Arrived"
                    date={logistic?.arrived_time}
                    active={!!logistic?.arrived_time}
                  />
                  <TimelineItem
                    title="Delivered"
                    date={logistic?.delivered_time}
                    active={!!logistic?.delivered_time}
                  />
                </div>
              </div>

              {/* Map */}
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  Delivery Route
                </h3>
                <div className="h-64 rounded-md overflow-hidden border border-gray-200">
                  <LoadScript googleMapsApiKey="AIzaSyD845dpQ62RHqNW83JcyA5YKaRQ05UVl8I">
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      center={{
                        lat: logistic?.pickup_location.latitude || 0,
                        lng: logistic?.pickup_location.longitude || 0,
                      }}
                      zoom={12}
                    >
                      <Marker
                        position={{
                          lat: logistic?.pickup_location.latitude || 0,
                          lng: logistic?.pickup_location.longitude || 0,
                        }}
                        label="P"
                      />
                      {logistic?.receiver.address.latitude && (
                        <Marker
                          position={{
                            lat: logistic?.receiver.address.latitude,
                            lng: logistic?.receiver.address.longitude,
                          }}
                          label="D"
                        />
                      )}
                    </GoogleMap>
                  </LoadScript>
                </div>
              </div>
            </div>

            {/* Right Column - Details */}
            <div>
              {/* Location Details */}
              <div className="bg-gray-50 rounded-lg p-5 mb-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  Location Details
                </h3>
                <div className="space-y-4">
                  <DetailCard
                    icon="📍"
                    title="Pickup Location"
                    items={[
                      {
                        label: "Address",
                        value: logistic?.pickup_location.address,
                      },
                      {
                        label: "Coordinates",
                        value: `${logistic?.pickup_location.latitude}, ${logistic?.pickup_location.longitude}`,
                      },
                    ]}
                  />
                  <DetailCard
                    icon="🏁"
                    title="Delivery Location"
                    items={[
                      {
                        label: "Address",
                        value: logistic?.receiver.address.address,
                      },
                      { label: "Receiver", value: logistic?.receiver.name },
                      {
                        label: "Receiver Phone",
                        value: logistic?.receiver?.phone || "N/A",
                      },
                    ]}
                  />
                  <DetailCard
                    icon="📦"
                    title="Package Info"
                    items={[
                      { label: "Item Size", value: logistic?.item_size },
                      {
                        label: "Description",
                        value: logistic?.description || "N/A",
                      },
                    ]}
                  />
                </div>
              </div>

              {/* People */}
              <div className="bg-gray-50 rounded-lg p-5 mb-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  People
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <PersonCard
                    id={logistic?.userId}
                    type="user"
                    title="Customer"
                    icon="👤"
                  />
                  <PersonCard
                    id={logistic?.agentId}
                    type="agent"
                    title="Delivery Agent"
                    icon="🚚"
                  />
                </div>
              </div>

              {/* Payment */}
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  Payment
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Method</span>
                    <span className="font-medium">
                      {logistic?.payment_method}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery Cost</span>
                    <span>₦{logistic?.cost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Fee</span>
                    <span>₦{logistic?.tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Discount</span>
                    <span>₦{logistic?.discount.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-3 mt-2 flex justify-between font-bold">
                    <span>Total</span>
                    <span>₦{logistic?.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Response Modal */}
      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          onClose(); // Close the details modal after response modal is closed
        }}
        type={modalType}
        message={modalMessage}
      />
    </div>
  );
};

export default LogisticDetails;

const TimelineItem = ({
  title,
  date,
  active = false,
}: {
  title: string;
  date?: string;
  active?: boolean;
}) => (
  <div className="flex items-start">
    <div
      className={`flex-shrink-0 w-3 h-3 mt-1.5 rounded-full ${active ? "bg-green-500" : "bg-gray-300"
        }`}
    ></div>
    <div className="ml-3">
      <p
        className={`text-sm font-medium ${active ? "text-gray-900" : "text-gray-500"
          }`}
      >
        {title}
      </p>
      {date && new Date(date).getFullYear() !== 1 ? (
        <p className="text-xs text-gray-500">
          {new Date(date).toLocaleString()}
        </p>
      ) : (
        <p className="text-xs text-gray-400">Pending</p>
      )}
    </div>
  </div>
);

const DetailCard = ({
  icon,
  title,
  items,
}: {
  icon: string;
  title: string;
  items: { label: string; value: string }[];
}) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <div className="flex items-center mb-2">
      <span className="text-xl mr-2">{icon}</span>
      <h4 className="font-medium">{title}</h4>
    </div>
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex justify-between text-sm">
          <span className="text-gray-500">{item.label}</span>
          <span className="font-medium text-right">{item.value}</span>
        </div>
      ))}
    </div>
  </div>
);

const PersonCard = ({
  id,
  type,
  title,
  icon,
}: {
  id: any;
  type: "user" | "agent";
  title: string;
  icon: string;
}) => {
  const finalId =
    typeof id === "string"
      ? id
      : Array.isArray(id) && id[0]?._id
        ? id[0]._id
        : null;

  return (
    <div className="border border-gray-200 rounded-lg p-4 flex items-center">
      <span className="text-2xl mr-3">{icon}</span>
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        {finalId ? (
          <Link
            to={`/pr-admin/${type}s/${type}-details/${finalId}`}
            className="font-medium hover:text-yellow-600 transition flex items-center gap-1 text-sm"
          >
            View Details
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        ) : (
          <p className="text-sm text-gray-400">No ID available</p>
        )}
      </div>
    </div>
  );
};
